import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Empresa,
  EmpresaListada,
  EntradaCrearEmpresa,
  EntradaEditarEmpresa,
  Sector,
  TrabajadorDeEmpresa,
} from '@copathy/shared';
import { api, apiSubir } from './api';

/**
 * Clientes de Copathy.
 *
 * ── Sobre los dos nombres ──
 * En la interfaz se llaman **Clientes**, porque quien usa esta pantalla es
 * personal de Copathy y para ellos eso son. En la base, en el contrato de API y
 * en los documentos siguen siendo `empresa` / `empresa_id`: está en 13 tablas, en
 * las políticas de RLS y en el contexto de cada transacción (D-035).
 *
 * **La costura es esta capa.** De acá para arriba —rutas, pantallas, textos— todo
 * dice Cliente. De acá para abajo —`/api/empresas`, los tipos de `shared`— todo
 * dice empresa. Por eso los tipos importados conservan su nombre: vienen del
 * contrato y renombrarlos acá escondería dónde está la traducción.
 */

export type Cliente = Empresa;
export type ClienteListado = EmpresaListada;

export type PaginaClientes = {
  datos: ClienteListado[];
  total: number;
  pagina: number;
  porPagina: number;
};

export type PaginaTrabajadores = {
  datos: TrabajadorDeEmpresa[];
  total: number;
  pagina: number;
  porPagina: number;
};

export type FiltroActivo = 'si' | 'no' | 'todas';

/** Lo que el `BuscadorFiltros` maneja: claves sueltas con valores CSV. */
export type FiltrosClientes = Record<string, string | undefined>;

export function useClientes(texto: string, filtros: FiltrosClientes) {
  const parametros = new URLSearchParams({ porPagina: '200' });
  if (texto.trim()) parametros.set('texto', texto.trim());
  // `activo` por omisión es 'si' en el servidor; si el filtro no lo trae, se
  // manda 'todas' para que «sin filtros» signifique de verdad «sin filtros».
  parametros.set('activo', filtros.activo ?? 'todas');
  if (filtros.pais) parametros.set('pais', filtros.pais);
  if (filtros.sector) parametros.set('sector', filtros.sector);

  return useQuery({
    queryKey: ['clientes', texto.trim(), filtros],
    queryFn: () => api.get<PaginaClientes>(`/api/empresas?${parametros.toString()}`),
    // El buscador escribe en la clave de la consulta: sin esto, cada tecla
    // vacía la lista y la pantalla parpadea entre «cargando» y resultados.
    placeholderData: (anterior) => anterior,
  });
}

/**
 * La ficha se pide aparte del listado.
 *
 * No es una optimización: trae el **código de registro** y su lectura queda
 * auditada (D-006). Traerla junto con el listado escribiría una fila de auditoría
 * por cada cliente cada vez que alguien abre la pantalla, y volvería inútil la
 * tabla que sirve justamente para saber quién miró qué.
 */
export function useCliente(id: number | null) {
  return useQuery({
    queryKey: ['cliente', id],
    queryFn: () => api.get<Cliente>(`/api/empresas/${id!}`),
    enabled: id !== null,
  });
}

/** Los usuarios del cliente. También queda auditado, así que va aparte de la ficha. */
export function useTrabajadoresDeCliente(id: number | null, activo: FiltroActivo) {
  return useQuery({
    queryKey: ['cliente-trabajadores', id, activo],
    queryFn: () =>
      api.get<PaginaTrabajadores>(`/api/empresas/${id!}/trabajadores?activo=${activo}&porPagina=200`),
    enabled: id !== null,
  });
}

function usarInvalidacion() {
  const qc = useQueryClient();
  return (id?: number) => {
    void qc.invalidateQueries({ queryKey: ['clientes'] });
    if (id !== undefined) void qc.invalidateQueries({ queryKey: ['cliente', id] });
  };
}

export function useGuardarCliente() {
  const invalidar = usarInvalidacion();

  const crear = useMutation({
    mutationFn: (datos: EntradaCrearEmpresa) => api.post<Cliente>('/api/empresas', datos),
    onSuccess: (c) => invalidar(c.id),
  });

  const editar = useMutation({
    mutationFn: (v: { id: number; cambios: EntradaEditarEmpresa }) =>
      api.patch<Cliente>(`/api/empresas/${v.id}`, v.cambios),
    onSuccess: (c) => invalidar(c.id),
  });

  return { crear, editar };
}

export function useCodigoRegistro() {
  const invalidar = usarInvalidacion();

  const rotar = useMutation({
    mutationFn: (v: { id: number; diasVigencia: number | null }) =>
      api.post<Cliente>(`/api/empresas/${v.id}/codigo`, { diasVigencia: v.diasVigencia }),
    onSuccess: (c) => invalidar(c.id),
  });

  const cerrar = useMutation({
    mutationFn: (id: number) => api.post<Cliente>(`/api/empresas/${id}/codigo/cerrar`),
    onSuccess: (c) => invalidar(c.id),
  });

  return { rotar, cerrar };
}

// ---------------------------------------------------------------------------
// Catálogo de sectores
// ---------------------------------------------------------------------------

export function useSectores(habilitado: boolean) {
  return useQuery({
    queryKey: ['sectores'],
    queryFn: () => api.get<Sector[]>('/api/sectores'),
    enabled: habilitado,
    // Cambia poquísimo: no tiene sentido volver a pedirlo en cada apertura del
    // formulario. Lo invalida quien crea uno nuevo.
    staleTime: 10 * 60_000,
  });
}

export function useCrearSector() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nombre: string) => api.post<Sector>('/api/sectores', { nombre }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sectores'] }),
  });
}

// ---------------------------------------------------------------------------
// Logotipo
// ---------------------------------------------------------------------------

export function useLogoCliente() {
  const invalidar = usarInvalidacion();

  const subir = useMutation({
    mutationFn: (v: { id: number; archivo: File }) =>
      apiSubir<Cliente>(`/api/empresas/${v.id}/logo`, 'logo', v.archivo),
    onSuccess: (c) => invalidar(c.id),
  });

  const quitar = useMutation({
    mutationFn: (id: number) => api.delete<Cliente>(`/api/empresas/${id}/logo`),
    onSuccess: (c) => invalidar(c.id),
  });

  return { subir, quitar };
}
